import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

const HEX_COLOR_RE = /^#[0-9a-fA-F]{3,8}$/;

export function safeHexColor(
	color: string | null | undefined,
	fallback = 'var(--muted-foreground)'
): string {
	return color && HEX_COLOR_RE.test(color) ? color : fallback;
}

/**
 * Returns a CSS color string with the given opacity applied.
 * Normalises any valid hex (#RGB, #RRGGBB, #RRGGBBAA) to an 8-digit hex.
 * Returns `fallback` for non-hex / falsy values.
 */
export function hexWithOpacity(
	color: string | null | undefined,
	opacity: number,
	fallback = 'transparent'
): string {
	if (!color || !HEX_COLOR_RE.test(color)) return fallback;
	let hex = color.replace('#', '');
	// Normalise 3-digit → 6-digit
	if (hex.length === 3) {
		hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
	}
	// Strip existing alpha (8-digit → 6-digit)
	if (hex.length === 8) {
		hex = hex.substring(0, 6);
	}
	const alpha = Math.round(Math.max(0, Math.min(1, opacity)) * 255)
		.toString(16)
		.padStart(2, '0');
	return `#${hex}${alpha}`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type WithoutChild<T> = T extends { child?: any } ? Omit<T, 'child'> : T;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type WithoutChildren<T> = T extends { children?: any } ? Omit<T, 'children'> : T;
export type WithoutChildrenOrChild<T> = WithoutChildren<WithoutChild<T>>;
export type WithElementRef<T, U extends HTMLElement = HTMLElement> = T & {
	ref?: U | null;
};
